<template>
  <div>
    <v-menu v-model="showMenu" bottom left origin="top right" @input="loadOptions">
      <template v-slot:activator="{ on, attrs }">
        <div class="clickable ml-2" v-bind="attrs" v-on="on" @click.stop.prevent>
          <v-icon class="m-0">mdi-dots-vertical</v-icon>
        </div>
      </template>

      <v-list class="px-3">
        <v-list-tile class="clickable" :class="{ disabled: !canCreateNewAnalysis }" @click="$emit('new-analysis')">
          <v-list-tile-title>New Analysis</v-list-tile-title>
        </v-list-tile>
        <v-list-tile class="clickable" @click="confirmDownload" v-if="getFlag('DAS-1416')">
          <v-list-tile-title>Cohort Info</v-list-tile-title>
        </v-list-tile>
        <v-divider />
        <v-list-tile class="clickable" :class="{ disabled: !canEdit }" @click="copyCohort">
          <v-list-tile-title>Copy Cohort</v-list-tile-title>
        </v-list-tile>
        <v-list-tile class="clickable" :class="{ disabled: !options || !options.isEditableByUser }" @click="editCohort">
          <v-list-tile-title>Edit Cohort</v-list-tile-title>
        </v-list-tile>
        <v-list-tile class="clickable" :class="{ disabled: !canArchive }" @click="showArchive = true">
          <v-list-tile-title>Archive Cohort</v-list-tile-title>
        </v-list-tile>
        <v-list-tile class="clickable" :class="{ disabled: !options || !options.isFresh }" @click="refreshCohort" v-if="isCreator">
          <v-list-tile-title>Refresh Cohort</v-list-tile-title>
        </v-list-tile>
        <v-divider />
        <v-list-tile class="clickable" @click="showPublishModal" v-if="isEversanaUser || isMainAdmin">
          <v-list-tile-title>Publish Cohort to Client</v-list-tile-title>
        </v-list-tile>
        <v-list-tile class="clickable" @click="$emit('hide-cohort', !item.isPrivate)" v-if="isCreator">
          <v-list-tile-title>{{ item.isPrivate ? 'Change Cohort to Public' : 'Change Cohort to Private' }}</v-list-tile-title>
        </v-list-tile>
        <v-list-tile class="clickable" @click="$emit('clinically-verify', !item.cohortVerified)" v-if="getFlag('is-user-able-to-clinically-verify-cohorts')">
          <v-list-tile-title>{{ item.cohortVerified ? 'Remove Clinical Verification' : 'Clinically Verify Cohort' }}</v-list-tile-title>
        </v-list-tile>
        <v-list-tile class="clickable" :class="{ disabled: item.cohortVerified || item.cohortVerificationPending }" @click="$emit('ask-clinically-verify')" v-if="isEversanaUser || isMainAdmin">
          <v-list-tile-title>Request to Clinically Verify the Cohort</v-list-tile-title>
        </v-list-tile>
        <v-list-tile class="clickable" @click="$emit('copy-url')">
          <v-list-tile-title>Get Cohort URL</v-list-tile-title>
        </v-list-tile>
        <v-divider />
        <v-list-tile class="clickable" @click="viewCohortSource">
          <v-list-tile-title>View Cohort Source</v-list-tile-title>
        </v-list-tile>
        <v-list-tile class="clickable" @click="viewCohortThoughtspot" v-if="getFlag('DAS-1380-ThoughtSpot')">
          <v-list-tile-title>Thoughtspot View </v-list-tile-title>
        </v-list-tile>
        <v-list-tile class="clickable" @click="viewCohortThoughtspotSage" v-if="getFlag('DAS-1380-ThoughtSpot')">
          <v-list-tile-title>ThoughtspotSage View </v-list-tile-title>
        </v-list-tile>

        <loader v-if="isLoading" />
      </v-list>
    </v-menu>

    <CohortArchiveDialog :name="item.name" v-model="showArchive" @archive="$emit('archive'), (showArchive = false)" @archive-all-versions="$emit('archive-all-versions'), (showArchive = false)" v-if="showArchive" />

    <!-- Pop-up Dialog for publishing cohorts -->
    <HvhModal
      :value="true"
      title="Publish Cohorts"
      max-width="720"
      @input="
        showPublish = false;
        clientsSelected = [];
      "
      v-if="showPublish"
    >
      <template v-slot>
        <v-select multiple chips :items="availableClients" label="Select the clients to publish this cohort" item-text="name" item-value="id" v-model="clientsSelected" return-object></v-select>
      </template>
      <template v-slot:actions>
        <v-btn
          color="primary"
          flat
          @click="
            $emit('publish', clientsSelected);
            showPublish = false;
          "
        >
          Add
        </v-btn>
        <v-btn
          flat
          @click="
            showPublish = false;
            clientsSelected = [];
          "
        >
          Cancel
        </v-btn>
      </template>
    </HvhModal>
  </div>
</template>

<script>
import axios from '../../../axios';
import { errorDebug } from '../../../libraries/common';
import CohortArchiveDialog from './CohortArchiveDialog.vue';
import ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';
export default {
  components: { CohortArchiveDialog },
  props: ['item', 'canEdit', 'noOfRelatedAnalytics', 'selectedCohortStatus', 'availableClients'],
  data() {
    return {
      showMenu: false,
      showArchive: false,
      options: null,
      isLoading: false,
      showPublish: false,
      clientsSelected: []
    };
  },
  computed: {
    isCreator() {
      return this.item.createdBy === this.$store.state.user.user.email;
    },
    canCreateNewAnalysis() {
      return this.canEdit && this.selectedCohortStatus === 'Done' && this.noOfRelatedAnalytics;
    },
    canArchive() {
      return !this.item.deletedOn && this.options?.isEditableByUser && this.selectedCohortStatus !== 'Processing';
    },
    versionIndex() {
      return this.options && this.options.allVersions.find(f => f.id === this.item.id).index;
    },
    isLastVersion() {
      return this.options && this.versionIndex === this.options.allVersions.max(f => f.index);
    },
    isEversanaUser() {
      return this.$store.state.user.client.name === 'Eversana';
    },
    isMainAdmin() {
      return this.$store.state.user.isMainAdmin;
    },
  },
  methods: {
    loadOptions() {
      if (!this.options) {
        this.isLoading = true;
        axios.get(`/Analytics/Cohorts/${this.item.id}/Options`)
          .then(({ data }) => this.options = data)
          .catch(errorDebug)
          .then(() => this.isLoading = false);
      }
    },
    copyCohort() {
      this.goTo(`/Analytics/Cohort/Create?fromCohortId=${this.item.id}`);
    },
    editCohort() {
      var goToCohort = () => this.goTo(`/Analytics/Cohort/${this.item.id}/Edit`);
      if (!this.isLastVersion) {
        this.$confirm('Confirm action', `You are about to edit an ${this.item.deletedOn ? 'archived' : 'older'} version of this cohort.  Do you want to continue?`)
          .then(goToCohort)
          .catch(() => null);
      } else goToCohort();
    },
    confirmDownload(){
        console.log("Hello");
        this.exportToExcel();
    },
    exportToExcel(){
        var data = this.item;
        console.log(this.item);
        var wb = new ExcelJS.Workbook();
        wb.addWorksheet('Cohort_info_details');
        const cohortInfoSheet = wb.getWorksheet('Cohort_info_details');
        cohortInfoSheet.columns = [
            { header: 'CohortID', key: 'CohortID' },
            { header: 'MINAGE', key: 'MINAGE' },
            { header: 'MAXAGE', key: 'MAXAGE' },
            { header: 'MINYEAR', key: 'MINYEAR' },
            { header: 'MAXYEAR', key: 'MAXYEAR' },
            { header: 'GENDER', key: 'GENDER' },
            { header: 'STATES', key: 'STATES' },
            { header: 'INCLUSIONCRITERIA', key: 'INCLUSIONCRITERIA' }
        ];
            cohortInfoSheet.addRow({
            CohortID: data.id,
            MINAGE: data.minAge,
            MAXAGE: data.maxAge,
            MINYEAR: data.minYear,
            MAXYEAR: data.maxYear,
            GENDER: data.gender,
            STATES: data.states,
            INCLUSIONCRITERIA: data.inclusionCriteria,
            });
        
        wb.xlsx.writeBuffer().then(buffer => {
            var blob = new Blob([buffer], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });

            saveAs(blob, `Cohort_info_details-${data.id}.xlsx`);

        });
    },
    refreshCohort() {
      this.$emit('refresh-cohort');
      // This commented code will be used in the future, do not remove id
      // this.$emit('save-cohort', {
      //   ...this.item,
      //   id: 0,
      //   patientDatasource: this.options.patientDataSourceId,
      //   selectedCohort: -1,
      // });
    },
    viewCohortSource() {
      window.open(`/Analytics/Cohort/GetCohortRawSql?cohortId=${this.item.id}`, '_blank');
    },
    showPublishModal() {
      this.showPublish = true;
      this.clientsSelected = this.item.clientsPublishedView.map(f => ({ name: f.clientName, id: f.clientId }));

    },
    viewCohortThoughtspot() {
      this.$router.push({ name: 'CohortThoughtspot', params: { cohortId: this.item.id } });
    },
    viewCohortThoughtspotSage() {
      this.$router.push({ name: 'CohortThoughtspotSage', params: { cohortId: this.item.id } });
    },
  }
}
</script>